import { AppId, Button, FCX, ProductLogo } from "@datassential/platform-ui-lib";
import './SectionCharts.scss';
import FoodSection from "@pages/Food/components/Section/Section";
import Widget from "@components/Widget/Widget";
import { iFoodProfileRecommendedCharts } from "@models/FoodProfileRecommendedCharts";
import cn from "classnames";
import { PermissionLevelEnum } from "@models/PermissionSection";
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import PermissionOverlay from "@pages/Food/components/PermissionOverlay/PermissionOverlay";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import { requestTrialByFeature } from "@actions/page";
import { FoodSectionEnum } from "@pages/Food/Food";
import http from "@core/fetch";

const FoodSectionCharts: FCX<{
    data: iFoodProfileRecommendedCharts;
    word: string;
    isLoading: boolean;
    setHasData: (hasData: boolean) => void;
}> = ({
    data: {
        data,
        permissionLevel,
    },
    isLoading,
    word,
    setHasData,
}) => {
    const dispatch = useDispatch();
    const requestTrial: (f: string, hideNotification?: boolean) => any = (featureName, hideNotification) => {
        if (!hideNotification) {
            toast.info('Request has been sent');
        }
        return dispatch(requestTrialByFeature(featureName));
    };

    const [isExpanded, setIsExpanded] = useState(false);

    const hasAccess = permissionLevel !== PermissionLevelEnum.None;

    useEffect(() => {
        setHasData(!isLoading && data.length > 0);
    }, [isLoading, data.length, setHasData]);

    const downloadReport = useCallback(
        (downloadLink: string) => {
            http.downloadFileXHRFromUrl('GET', downloadLink)
                .then(() => {
                    toast.success('Report downloaded successfully');
                })
                .catch(() => {
                    toast.error('Failed to download report');
                });
        },
        []
    );

    if (isLoading) {
        return (
            <div className="Food__section is-loading">
                <div className="spinner spinner-blink">
                    <ProductLogo appId={AppId.Snap} type="regular"/>
                </div>
            </div>
        );
    }

    if (data.length === 0) return null;

    return (
        <div
            className="Food__section" id={FoodSectionEnum.Charts}
            data-testid="blockFoodSectionCharts"
        >
            <FoodSection
                testId="blockFoodCharts"
                headerContent={(
                    <div data-testid="textFoodChartsTitle">
                        CHARTS
                    </div>
                )}
                className="appears-top del-2"
            >
                <Widget className={cn("FoodSectionCharts", !hasAccess && 'is-blocked')}>
                    <div className={cn(
                        "FoodSectionCharts__content",
                        !isExpanded && 'is-collapsed',
                        "FoodSectionCharts__blur",
                    )}>
                        {data.map((item, index) => {
                            const props = {
                                key: item.title,
                                className: cn(
                                    "FoodSectionCharts__item",
                                    !!item.link && 'is-link',
                                ),
                                title: `${item.title} (${item.typeLabel})`,
                                'data-testid': `buttonFoodChartsItemLink-${index}`
                            };
                            const titleContent = (
                                <>
                                    <div
                                        className="FoodSectionCharts__item-title"
                                        data-testid={`textFoodChartsItemTitle-${index}`}
                                    >
                                        {item.title}
                                    </div>
                                    <div
                                        className="FoodSectionCharts__item-subtitle"
                                        data-testid={`textFoodChartsItemSubtitle-${index}`}
                                    >
                                        ({item.typeLabel})
                                    </div>
                                    {(item.downloadOptions ?? []).map(i => (
                                        <button
                                            key={i.title}
                                            className="FoodSectionCharts__item-button"
                                            onClick={() => downloadReport(i.url)}
                                        >
                                            {i.title}
                                        </button>
                                    ))}
                                </>
                            );

                            if (!item.link) {
                                return (
                                    <div {...props}>
                                        {titleContent}
                                    </div>
                                );
                            }

                            return (
                                <Link
                                    {...props}
                                    to={item.link}
                                >
                                    {titleContent}
                                </Link>
                            );
                        })}
                    </div>
                    <div className="FoodSectionCharts__footer">
                        {data.length > 14 && (
                            <Button
                                onClick={() => setIsExpanded(!isExpanded)}
                                className="FoodSectionCharts__button"
                                data-testid="buttonFoodChartsToggleMore"
                            >
                                {isExpanded ? 'Show less' : 'Show more'}
                            </Button>
                        )}
                    </div>
                    {!hasAccess && (
                        <PermissionOverlay
                            onContactUsClick={() => requestTrial('mtd')}
                            testId="blockFoodChartsPermissionOverlay"
                        >
                            <div data-testid="textFoodChartsPermissionOverlayContentLine-0">
                                Upgrade your subscription to unlock
                            </div>
                            <div data-testid="textFoodChartsPermissionOverlayContentLine-1">
                                analyses featuring <b>{word}</b>
                            </div>
                        </PermissionOverlay>
                    )}
                </Widget>
            </FoodSection>
        </div>
    );
};

export default FoodSectionCharts;